import React from 'react'
import { fetcher } from '@/utils/fetcher'
import useSWR, { useSWRConfig } from 'swr'
import { NETWORK, API_URL } from '@/utils/constants'

const OVERRIDES = {
	DOGE: {
		halfHourFee: 500000,
		hourFee: 400000,
		fastestFee: 600000,
		minimumFee: 200000,
		economyFee: 300000
	},
	SIGNET: {
		halfHourFee: 5,
		hourFee: 1,
		fastestFee: 1,
		minimumFee: 1,
		economyFee: 1
	}
}
export const BTC_FALLBACK = {
	halfHourFee: 1,
	hourFee: 1,
	fastestFee: 1,
	minimumFee: 1,
	economyFee: 1
}

interface Speed {
	slow: number
	medium: number
	fast: number
}

interface Rate {
	economyFee: number
	halfHourFee: number
	fastestFee: number
	hourFee: number
	minimumFee: number
}

function classifyRates(data: any): Speed {
	return { slow: data?.economyFee || data?.halfHourFee, medium: data?.halfHourFee, fast: data?.fastestFee }
}

const key = 'ordinals_wallet::selected_fee_level'
const FEE_LEVELS = new Set(['slow', 'medium', 'fast'])

export function normalizeFeeLevel(level: string | null | undefined) {
	return level && FEE_LEVELS.has(level) ? level : 'medium'
}

export function resolveFeeEstimateState({
	selectedRaw,
	data,
	override,
	network
}: {
	selectedRaw?: string | null
	data: Rate | undefined
	override?: Rate
	network: string
}) {
	const selected = normalizeFeeLevel(selectedRaw)
	const processedData = override || data || (network === 'BTC' ? BTC_FALLBACK : data)
	const speed = classifyRates(processedData)

	return {
		selected,
		data: processedData,
		speed,
		feeRate: (speed as any)?.[selected] ?? 0,
		// No live estimate backs these numbers: BTC_FALLBACK's 1 sat/vB stands
		// in for every tier. Flows that lock a fee into something that cannot
		// be bumped later must not commit it silently.
		isFallback: !override && !data && network === 'BTC'
	}
}

export const MEMPOOL_FEES_URL = 'https://mempool.space/api/v1/fees/recommended'

// Our API's electrum-backed estimates are the primary source (same shape as
// mempool.space's /fees/recommended). mempool.space is only the fallback: it
// is a third party fetched from the browser, and an outage or a user-side
// block (2026-07-07: users couldn't list/delist/split all morning) used to
// kill every fee-dependent flow. BTC_FALLBACK stays as the last resort via
// resolveFeeEstimateState when both sources fail.
export const btcFeesFetcher = async (url: string) => {
	try {
		const data = await fetcher(url)
		if (!data?.fastestFee) throw new Error('malformed fee response')
		return data
	} catch {
		const data = await fetcher(MEMPOOL_FEES_URL)
		if (!data?.fastestFee) throw new Error('malformed fee response')
		return data
	}
}

const selectedFetcher = () => {
	const selected = normalizeFeeLevel(localStorage.getItem(key))
	if (localStorage.getItem(key) !== selected) {
		localStorage.setItem(key, selected)
	}

	return selected
}


export const useSelectFeeRate = () => {
	const { mutate } = useSWRConfig()
	const selectFee = (level: any) => {
		localStorage.setItem(key, normalizeFeeLevel(level))
		mutate(key)
	}

	return selectFee
}

export default function useFeeEstimates(): {
	data: Rate
	speed: Speed
	feeRate: number
	isLoading: boolean
	selected: string
	/** True while `data` is the 1 sat/vB BTC_FALLBACK because both fee sources failed (or have not answered yet). */
	isFallback: boolean
} {
	const { data: selectedRaw } = useSWR(key, selectedFetcher)
	const override = (OVERRIDES as any)[NETWORK]

	const url = React.useMemo(() => {
		if (override) return null
		return `${API_URL}/wallet/fee-estimates`
	}, [override])

	const { data, isLoading } = useSWR(url, NETWORK === 'BTC' ? btcFeesFetcher : fetcher)

	const resolved = React.useMemo(
		() =>
			resolveFeeEstimateState({
				selectedRaw,
				data: data as Rate | undefined,
				override,
				network: NETWORK
			}),
		[data, override, selectedRaw]
	)

	return {
		selected: resolved.selected,
		data: resolved.data as Rate,
		isLoading: override ? false : isLoading,
		speed: resolved.speed,
		feeRate: resolved.feeRate,
		isFallback: resolved.isFallback
	}
}
